import { InsuranceName } from '@orcar/common';
import {
  ALERT_MESSAGE_TYPE,
  authAtom,
  cn,
  convertToKST,
  CustomerForm,
  // [비활성화] 할인코드 DiscountArea
  // DiscountArea,
  ENVIRONMENT,
  getDiscountList,
  getInsuranceCoverageLimit,
  getPrimaryColor,
  // [비활성화] 할인코드 DiscountArea에서 쓰던 사전예약 재조회
  // getPreReservation,
  getSureUserId,
  INSURANCE_TYPE,
  isMobileAtom,
  JEJURO,
  ListStyle,
  MUTATION_KEY,
  NaverPayIcon,
  PAGE_NAME,
  PaycoIcon,
  PAYMENT_METHOD_ID,
  PAYMENT_METHOD_LABEL,
  PaymentInfoCard,
  PaymentMethodArea,
  SESSION_STORAGE_KEY,
  simplifyPriceFormat,
  TossPayIcon,
  URLs,
  useCreatePreReservation,
  useCustomerForm,
  useGetCompanyInfo,
  useGetDiscountPolicy,
  useGetMileageBalance,
  useGetPreReservation,
  useGetReferralDiscount,
  useGetVehicleModel,
  useGetVehicleModels,
  useRecoil,
  useUpdatePreReservation,
  Viewer,
} from '@orcar/common-d2c';
import { useQueryClient } from '@tanstack/react-query';
import { loadTossPayments } from '@tosspayments/payment-sdk';
import dayjs from 'dayjs';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { getInsurancePerDay, getRentalDays } from '@/constants/experiment';
import { SUPERCAR_RENTAL_AGE_NOTICE } from '@/constants/rental-rules';
import {
  trackInsuranceOptionDeselected,
  trackInsuranceOptionSelected,
  trackInsuranceSectionViewed,
  trackPageView,
  trackPaymentFailed,
  trackPaymentStarted,
  trackPaymentSuccessful,
  trackReferralDiscountApplied,
} from '@/utils/analytics';
import {
  getCurrentInsuranceRange,
  getInsuranceName,
  getInsuranceQualification,
} from '@/utils/insurance';
import { computePromo } from '@/utils/promo';
import {
  EMPTY_PET_FORM,
  isPetFormValid,
  PetFormValue,
  PetInfoForm,
  toPetUpdateInput,
} from './components/PetInfoForm';
import InfluencerCodeArea from './InfluencerCodeArea';
import MileageArea from './MileageArea';
import ReferralCodeArea from './ReferralCodeArea';
// 인플루언서 할인코드(주유권) — 가격 변동 없이 반납 후 주유권 대상 표시만.
import {
  InsuranceTier,
  InsuranceTierSelector,
} from '../components/InsuranceTierSelector';
import {
  VehicleDetailCard,
  VehicleOptionSection,
} from '../components/VehicleDetailCard';

const CLIENT_KEY = import.meta.env.VITE_TOSS_CLIENT_KEY;
const ORIGIN_URL = import.meta.env.VITE_ORIGIN_URL;

/** 대여규정 미리보기(접힘) 높이(px). 이보다 길면 "설명 더보기"로 펼친다. */
const RENT_POLICY_PREVIEW_HEIGHT = 160;

const PAYMENT_METHODS = [
  {
    id: PAYMENT_METHOD_ID.NAVER_PAY,
    label: PAYMENT_METHOD_LABEL.NAVER_PAY,
    iconImgUrl: NaverPayIcon,
    iconSize: 65,
  },
  {
    id: PAYMENT_METHOD_ID.TOSS_PAY,
    label: PAYMENT_METHOD_LABEL.TOSS_PAY,
    iconImgUrl: TossPayIcon,
    iconSize: 84,
  },
  {
    id: PAYMENT_METHOD_ID.PAYCO,
    label: PAYMENT_METHOD_LABEL.PAYCO,
    iconImgUrl: PaycoIcon,
    iconSize: 53,
  },
  {
    id: PAYMENT_METHOD_ID.NORMAL,
    label: PAYMENT_METHOD_LABEL.NORMAL,
  },
  {
    id: PAYMENT_METHOD_ID.BUSINESS,
    label: PAYMENT_METHOD_LABEL.BUSINESS,
  },
  {
    id: PAYMENT_METHOD_ID.ACCOUNT_TRANSFER,
    label: PAYMENT_METHOD_LABEL.ACCOUNT_TRANSFER,
  },
];

const PaymentLayout = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sureUserId = getSureUserId();

  const { recoilValue: isMobile } = useRecoil(isMobileAtom);
  const { recoilValue: user } = useRecoil(authAtom);

  const [paymentMethod, setPaymentMethod] = useState<PAYMENT_METHOD_ID>(
    PAYMENT_METHOD_ID.NAVER_PAY,
  );
  // 할인코드(DiscountArea)·친구추천은 계속 비활성 — 값 고정.
  const appliedReferralCode = '';
  // 재방문 마일리지는 복구(사용). 단 차량 할인 60% 하한을 넘지 않도록 상한을 건다.
  const [appliedMileage, setAppliedMileage] = useState(0);
  // 인플루언서 쿠폰코드(가격 영향 없음). 결제 확정 시 예약 메모에 태그로 전달.
  const [influencerCode, setInfluencerCode] = useState('');
  const [isLoadingTossClient, setIsLoadingTossClient] = useState(false);
  const [isFullCoverage, setIsFullCoverage] = useState(false);
  const [petForm, setPetForm] = useState<PetFormValue>(EMPTY_PET_FORM);
  // 결제 페이지 하단 대여규정: 기본은 미리보기(접힘)로 보여주고 "설명 더보기"로 펼친다.
  const [isRentPolicyOpen, setIsRentPolicyOpen] = useState(false);
  const rentPolicyRef = useRef<HTMLDivElement>(null);
  // 모바일 하단 고정 결제바 → 약관 미동의 시 결제 카드로 스크롤 이동한다.
  const paymentCardRef = useRef<HTMLDivElement>(null);
  // 내용이 미리보기 높이를 넘을 때만 페이드 + 더보기 버튼을 노출한다.
  const [isRentPolicyOverflowing, setIsRentPolicyOverflowing] = useState(false);

  // 보험 단계 변경 시 사전예약을 재생성하므로 ID를 상태로 관리한다.
  const [preReservationId, setPreReservationId] = useState(
    sessionStorage.getItem(SESSION_STORAGE_KEY.PRE_RESERVATION_ID) ?? '',
  );

  const { data: preReservationData } = useGetPreReservation(preReservationId, {
    referralCode: appliedReferralCode,
    isFullCoverage,
    sureUserId: sureUserId ? Number(sureUserId) : undefined,
  });
  const { data: vehicleModelData } = useGetVehicleModel({
    id: preReservationData?.vehicleModelId,
    params: {
      pickUpAt: convertToKST(preReservationData?.pickUpAt),
      dropOffAt: convertToKST(preReservationData?.dropOffAt),
      insuranceName: preReservationData?.insuranceName,
      insuranceId: preReservationData?.insuranceId ?? undefined,
      isWithPet: preReservationData?.isWithPet,
    },
  });
  const { data: discountPolicyData } = useGetDiscountPolicy();
  const { data: companyData } = useGetCompanyInfo();

  // 재방문 마일리지 + 친구추천 (회원 전용). 둘 다 차량료를 깎으므로 60% 하한을
  // 합산 기준으로 지킨다: (친구추천 + 마일리지) ≤ 차량 할인 여력.
  const { data: mileageBalanceData } = useGetMileageBalance({
    enabled: !!user,
  });
  const mileageBalance = mileageBalanceData?.balance ?? 0;
  const { data: referralDiscountData, refetch: refetchReferralDiscount } =
    useGetReferralDiscount({ enabled: !!user });

  // 차량 할인 하한: 고시제상 차량료는 상한가(정가) 대비 최대 60%까지만 할인 가능.
  // 상한가 = referencePrice(=declaredPrice 정가, 예약기간 스케일). 할인 여력은
  // 반드시 "제공가(offeredPrice, 정산수수료 미반영)" 기준으로 잡는다 —
  // listPrice(정산가)는 정산수수료(~2%)가 더해져 있어 그걸로 여력을 계산하면
  // 그 2%만큼 여력이 새(=60%를 넘겨 할인됨). 값 미로딩 시 여력 0으로 방어한다.
  const referencePrice = vehicleModelData?.referencePrice ?? 0;
  const offeredPrice = preReservationData?.offeredPrice ?? 0;
  const vehicleFloor = Math.floor(referencePrice * 0.4);
  const vehicleDiscountRoom =
    referencePrice > 0 && offeredPrice > 0
      ? Math.max(0, offeredPrice - vehicleFloor)
      : 0;

  // 친구추천(자동, 첫 예약 5,000원)을 먼저 하한 여력 안에서 적용한다.
  const referralDiscount = Math.min(
    referralDiscountData?.amount ?? 0,
    vehicleDiscountRoom,
    preReservationData?.price ?? 0,
  );
  // 추천 할인이 처음 유효해진 순간 1회만 트래킹.
  const referralTrackedRef = useRef(false);
  useEffect(() => {
    if (referralDiscount > 0 && !referralTrackedRef.current) {
      referralTrackedRef.current = true;
      trackReferralDiscountApplied({ discount_amount: referralDiscount });
    }
  }, [referralDiscount]);

  // 마일리지는 남은 여력(하한 − 친구추천) 안에서 사용. 추가로 잔액·결제액 50% 제한.
  const mileageCap = Math.min(
    mileageBalance,
    Math.floor((preReservationData?.price ?? 0) * 0.5),
    Math.max(0, vehicleDiscountRoom - referralDiscount),
  );
  // 보험/기간 변경으로 상한이 바뀌면 초과 사용액을 보정한다(1,000P 미만은 0).
  useEffect(() => {
    setAppliedMileage((prev) => {
      const clamped = Math.min(prev, mileageCap);
      return clamped >= 1000 ? clamped : 0;
    });
  }, [mileageCap]);

  const finalPrice =
    (preReservationData?.price ?? 0) - appliedMileage - referralDiscount;
  // 대여요금(차량료) 정가(기준요금 원가) 대비 할인 — 검색 카드와 동일 기준.
  const rentalPromo = computePromo(
    vehicleModelData?.referencePrice,
    preReservationData?.listPrice,
  );
  // 마일리지·친구추천 할인분을 할인 목록에 노출.
  const discountListWithMileage = [
    ...getDiscountList(preReservationData?.tempDiscounts),
    ...(appliedMileage > 0
      ? [
          {
            item: `마일리지 ${appliedMileage.toLocaleString()}P 사용`,
            detail: `-${appliedMileage.toLocaleString()}`,
            type: 'supplementary' as const,
          },
        ]
      : []),
    ...(referralDiscount > 0
      ? [
          {
            item: '친구추천 할인',
            detail: `-${referralDiscount.toLocaleString()}`,
            type: 'supplementary' as const,
          },
        ]
      : []),
  ];

  // 보험 3단계 카드에 단계별 가격을 표시하기 위해 해당 차량의 insurances[]를 조회한다.
  const { data: tierVehicleList } = useGetVehicleModels({
    params: {
      pickUpAt: convertToKST(preReservationData?.pickUpAt),
      dropOffAt: convertToKST(preReservationData?.dropOffAt),
      driverAge: undefined,
      numberOfPeople: undefined,
      drivingExperience: undefined,
      isWithPet: preReservationData?.isWithPet,
      sureUserId: sureUserId ? Number(sureUserId) : undefined,
    },
  });
  const tierVehicle = tierVehicleList?.find(
    (vehicle) => vehicle.id === preReservationData?.vehicleModelId,
  );

  const { mutate: createPreReservation } = useCreatePreReservation();
  // 보험 단계 변경(사전예약 재생성) 진행 여부
  const [isSwitchingTier, setIsSwitchingTier] = useState(false);

  // 대여규정 미리보기 접힘 높이. 내용이 이보다 길 때만 "설명 더보기"를 노출한다.
  useLayoutEffect(() => {
    const el = rentPolicyRef.current;
    if (!el) return;
    setIsRentPolicyOverflowing(
      el.scrollHeight > RENT_POLICY_PREVIEW_HEIGHT + 8,
    );
  }, [companyData?.rentalRules, isMobile]);

  const { mutate: updatePreReservation } = useUpdatePreReservation();

  const isMutatingUpdatePreReservation =
    queryClient.isMutating({
      mutationKey: [MUTATION_KEY.UPDATE_PRE_RESERVATION],
    }) > 0;

  const { customerFormProps, checked, handleCheckbox } = useCustomerForm({
    user,
    options: { requireEmail: true },
  });

  const isSingleDayRental = dayjs(preReservationData?.pickUpAt).isSame(
    dayjs(preReservationData?.dropOffAt),
    'date',
  );
  const insuranceName = preReservationData?.insuranceName;
  const isSuperInsurance = insuranceName === INSURANCE_TYPE.SUPER;
  const canAddUnlimitedOption = isSingleDayRental
    ? false
    : vehicleModelData?.insurance.insuranceName === INSURANCE_TYPE.NORMAL
      ? !!vehicleModelData?.availableWithInsuranceOption1
      : vehicleModelData?.insurance.insuranceName === INSURANCE_TYPE.PERFECT
        ? !!vehicleModelData?.availableWithInsuranceOption2
        : false;

  // [비활성화] 할인코드(DiscountArea)에서 쓰던 서버 할인코드 조회
  // const discountCode = preReservationData?.tempDiscounts.find(
  //   (discount) => discount?.category === D2CDiscountCategory.DISCOUNT_CODE,
  // );

  // 페이지 방문 이벤트 트래킹
  useEffect(() => {
    if (preReservationData && vehicleModelData) {
      trackPageView('payment', {
        reservation_id: preReservationData.id,
        car_id: String(vehicleModelData.id),
        car_model: vehicleModelData.name,
        car_type: vehicleModelData.type,
        insurance_name: preReservationData.insuranceName,
        total_amount: preReservationData.price,
      });
    }
  }, [preReservationData, vehicleModelData]);

  const handlePayment = async () => {
    if (!preReservationData) {
      alert(ALERT_MESSAGE_TYPE.INVALID_RESERVATION);
      navigate(PAGE_NAME.search);
      return;
    }
    setIsLoadingTossClient(true);

    if (appliedReferralCode) {
      sessionStorage.setItem(
        SESSION_STORAGE_KEY.REFERRAL_CODE,
        appliedReferralCode,
      );
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY.REFERRAL_CODE);
    }

    if (isFullCoverage) {
      sessionStorage.setItem(SESSION_STORAGE_KEY.IS_FULL_COVERAGE, 'true');
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY.IS_FULL_COVERAGE);
    }

    // 마일리지 사용액은 리다이렉트 후 confirm에서 쓰이므로 세션에 보관한다.
    if (appliedMileage > 0) {
      sessionStorage.setItem(
        SESSION_STORAGE_KEY.USED_MILEAGE,
        String(appliedMileage),
      );
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY.USED_MILEAGE);
    }

    // 친구추천 할인 적용 여부도 세션에 보관(리다이렉트 후 confirm에서 전송).
    if (referralDiscount > 0) {
      sessionStorage.setItem(SESSION_STORAGE_KEY.APPLY_REFERRAL, 'true');
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY.APPLY_REFERRAL);
    }

    const tossPayments = await loadTossPayments(CLIENT_KEY);
    const commonOptions = {
      amount: preReservationData.price - appliedMileage - referralDiscount,
      orderId: preReservationData.id,
      orderName: preReservationData.vehicleModel.name || '',
      customerName: customerFormProps.nameInput.value,
      successUrl: `${ORIGIN_URL}${PAGE_NAME.request_payment}`,
      failUrl: `${ORIGIN_URL}${PAGE_NAME.payment_fail}`,
    };

    const handleSuccessPayment = () => {
      // 결제 성공 이벤트 트래킹
      trackPaymentSuccessful({
        reservation_id: preReservationData.id,
        car_id: String(vehicleModelData?.id),
        car_type: vehicleModelData?.type || '',
        total_amount: preReservationData.price,
        is_with_pet: preReservationData.isWithPet,
        pet_type: preReservationData.petType,
        pet_count: preReservationData.petCount,
        pet_price_rate: preReservationData.petPriceRate,
      });
      setIsLoadingTossClient(false);
    };

    const handleCancelPayment = (error: any) => {
      // 결제 실패 이벤트 트래킹
      trackPaymentFailed({
        reservation_id: preReservationData.id,
        error_code: error.code,
        error_message: error.message,
      });

      if (error.code === 'USER_CANCEL') {
        console.log('결제가 취소되었어요.');
      } else if (error.code === 'INVALID_CARD_COMPANY') {
        alert(ALERT_MESSAGE_TYPE.INVALID_CARD);
      }
      setIsLoadingTossClient(false);
    };

    if (
      paymentMethod === PAYMENT_METHOD_ID.NORMAL ||
      paymentMethod === PAYMENT_METHOD_ID.BUSINESS
    ) {
      tossPayments
        .requestPayment('카드', commonOptions)
        .then(handleSuccessPayment)
        .catch(handleCancelPayment);
    } else if (paymentMethod === PAYMENT_METHOD_ID.ACCOUNT_TRANSFER) {
      tossPayments
        .requestPayment('계좌이체', commonOptions)
        .then(handleSuccessPayment)
        .catch(handleCancelPayment);
    } else if (
      paymentMethod === PAYMENT_METHOD_ID.NAVER_PAY ||
      paymentMethod === PAYMENT_METHOD_ID.TOSS_PAY ||
      paymentMethod === PAYMENT_METHOD_ID.PAYCO
    ) {
      tossPayments
        .requestPayment('카드', {
          ...commonOptions,
          flowMode: 'DIRECT',
          easyPay: paymentMethod,
        })
        .then(handleSuccessPayment)
        .catch(handleCancelPayment);
    } else {
      return setIsLoadingTossClient(false);
    }
  };

  // 완전면책 가격 실험 측정용 관측값(화면에 노출된 가격 그대로)
  const rentalDays = getRentalDays(
    preReservationData?.pickUpAt,
    preReservationData?.dropOffAt,
  );
  const superInsuranceTotal =
    preReservationData?.pricingVersion === 1
      ? preReservationData.insurancePrice
      : preReservationData?.fullCoverageInsurancePrice;

  // 자차보험 섹션 노출(차량 1건당 1회) — 완전면책 UX 성과 측정용.
  // 차량 상세 페이지 제거로, 기존 order_confirmation 단계 측정을 결제 단계로 이전한다.
  useEffect(() => {
    if (!vehicleModelData?.id) return;
    trackInsuranceSectionViewed({
      car_id: String(vehicleModelData.id),
      car_type: vehicleModelData.type,
      base_insurance: insuranceName,
      can_add_super: canAddUnlimitedOption,
      rental_days: rentalDays,
      insurance_price_total: superInsuranceTotal,
      insurance_price_per_day: getInsurancePerDay(
        superInsuranceTotal,
        rentalDays,
      ),
      is_member: !!user,
      step: 'payment',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleModelData?.id]);

  // 반려동물 동반("펫과 함께") 예약 여부는 예약 생성 시 서버에 저장된 값으로 판별한다.
  const isWithPet = preReservationData?.isWithPet ?? false;

  const handleClickPayButton = () => {
    trackPaymentStarted();
    // 펫 동반 예약은 필수 정보 입력 + 이용 조건 동의가 없으면 결제로 진행할 수 없다.
    if (isWithPet && !isPetFormValid(petForm)) {
      alert('반려동물 동반 정보를 모두 입력하고 이용 조건에 동의해주세요.');
      return;
    }

    // 인플루언서 쿠폰코드는 토스 리다이렉트 이후(RequestPaymentPage) 확정 메모로
    // 쓰이므로 세션에 실어 넘긴다(가격에는 영향 없음).
    if (influencerCode) {
      sessionStorage.setItem('jejuro_influencer_code', influencerCode);
    } else {
      sessionStorage.removeItem('jejuro_influencer_code');
    }

    // 고객 정보(+펫 정보)를 사전예약에 반영한 뒤 곧바로 토스 결제창을 띄운다.
    // 기존 운전자 나이 확인 모달 · 최종 동의 모달 단계는 제거했고, 필수 약관 동의는
    // 결제 카드의 인라인 체크박스로 대체한다(체크 전에는 결제 버튼이 비활성화됨).
    updatePreReservation(
      {
        id: preReservationId,
        input: {
          customerName: customerFormProps.nameInput.value,
          customerPhoneNumber: customerFormProps.phoneNumberInput.value,
          customerEmail: customerFormProps.emailInput.value,
          ...(isWithPet ? toPetUpdateInput(petForm) : {}),
        },
      },
      {
        onSuccess: () => {
          handlePayment();
        },
        onError: () => {
          alert(ALERT_MESSAGE_TYPE.ERROR_OCCURRENCE);
        },
      },
    );
  };

  // ── 보험 카드: 차량 기본 자차(일반/고급 중 하나) + 완전면책 ──
  // 가격은 insurances[], 보장 설명은 우리 보험 값 기반 간결 표기.
  const priceOfTier = (name: string) =>
    tierVehicle?.insurances.find((i) => i.insuranceName === name);

  const insuranceTiers: InsuranceTier[] = (tierVehicle?.insurances ?? []).map(
    (plan) => {
      const info = plan.insuranceInfo;
      const isSuper = plan.insuranceName === INSURANCE_TYPE.SUPER;
      // 보상한도: 완전면책은 자차손해 전액 보장이므로 항상 '무제한'으로 표기한다.
      // (보험안내·이용안내 페이지 문구와 동일. ERP 저장값은 '자차손해100%'로 같은 뜻이지만
      //  고객이 한도가 있는 것으로 오인해 문의가 발생했다.)
      // 그 외 등급은 pricingVersion 1이면 서버 값, 아니면 보험명 기준 계산.
      const coverageLimit = isSuper
        ? '무제한'
        : plan.pricingVersion === 1
          ? (info?.limit ?? '-')
          : simplifyPriceFormat(
              getInsuranceCoverageLimit(
                plan.insuranceName,
                vehicleModelData?.insurance1?.limit,
                vehicleModelData?.insurance2?.limit,
              ),
            ) || '-';
      // 대여 자격(나이/경력)은 차량·등급 기준 규칙으로 고지한다.
      const qualification = getInsuranceQualification(
        plan.insuranceName,
        vehicleModelData?.type,
        vehicleModelData?.name,
      );
      // 보장 설명은 항상 노출한다. 서버 insuranceInfo가 없으면 보험명 기준 기본값으로 대체.
      // 가입을 위축시키는 자기부담금 대신 보상한도·단독사고를 강조 배치한다.
      return {
        insuranceName: plan.insuranceName,
        label: getInsuranceName(plan.insuranceName),
        variant: isSuper ? 'premium' : 'basic',
        additionalPrice: plan.insurancePrice,
        lines: [
          { label: '보상한도', value: coverageLimit, emphasis: 'strong' },
          {
            label: '단독사고',
            value:
              getCurrentInsuranceRange('단독 사고', plan.insuranceName) || '-',
            emphasis: 'strong',
          },
          {
            label: '제한나이',
            value: `만 ${qualification.minAge}세 이상`,
          },
          {
            label: '운전경력',
            value: `${qualification.minExperience}년 이상`,
          },
          {
            // 자기부담금이 흐려서 잘 안 보인다는 CS 요청 → 제한나이·운전경력과
            // 동일하게 기본(또렷한) 스타일로 노출(emphasis 미지정).
            label: '자기부담금',
            value:
              info?.indemnification ??
              getCurrentInsuranceRange('자기부담금', plan.insuranceName) ??
              '-',
          },
          {
            label: '휴차보상료',
            value:
              info?.compensation ??
              getCurrentInsuranceRange('휴차 보상료', plan.insuranceName) ??
              '-',
          },
        ],
      };
    },
  );

  const selectedTier = insuranceName ?? '';

  // 회원/비회원 가격 비교용: 선택 보험의 정가(비회원)−회원가 차액.
  // 마일리지·추천 할인은 회원/비회원 동일하므로, 이 차액이 곧 최종금액의 차이가 된다.
  const selectedInsurancePrice = priceOfTier(selectedTier);
  const memberBenefitAmount =
    selectedInsurancePrice?.userFinalPrice != null &&
    selectedInsurancePrice.userFinalPrice > 0
      ? Math.max(
          0,
          selectedInsurancePrice.finalPrice -
            selectedInsurancePrice.userFinalPrice,
        )
      : 0;

  // 보험 단계 선택 → 사전예약을 해당 보험으로 재생성하고 가격을 갱신한다.
  const handleSelectTier = (name: string) => {
    if (!preReservationData || name === insuranceName || isSwitchingTier)
      return;
    // 완전면책 실험: 슈퍼 선택/해제 시점 트래킹(완료 이벤트가 최종 집계 기준).
    const wasSuper = insuranceName === INSURANCE_TYPE.SUPER;
    const willSuper = name === INSURANCE_TYPE.SUPER;
    setIsSwitchingTier(true);
    // 완전면책는 자체 보험명(SUPER)이므로 무제한 특약 add-on 플래그는 사용하지 않는다.
    setIsFullCoverage(false);
    sessionStorage.removeItem(SESSION_STORAGE_KEY.IS_FULL_COVERAGE);
    createPreReservation(
      {
        vehicleModelId: preReservationData.vehicleModelId,
        pickUpAt: convertToKST(preReservationData.pickUpAt),
        dropOffAt: convertToKST(preReservationData.dropOffAt),
        insuranceName: name as InsuranceName,
        insuranceId: priceOfTier(name)?.insuranceId ?? undefined,
        isWithPet: preReservationData.isWithPet || undefined,
      },
      {
        onSuccess: (res) => {
          sessionStorage.setItem(
            SESSION_STORAGE_KEY.PRE_RESERVATION_ID,
            res.id,
          );
          setPreReservationId(res.id);
          setIsSwitchingTier(false);

          const insuranceEventProps = {
            car_id: vehicleModelData ? String(vehicleModelData.id) : undefined,
            car_type: vehicleModelData?.type,
            base_insurance: name,
            rental_days: rentalDays,
            is_member: !!user,
            step: 'payment' as const,
          };
          if (willSuper && !wasSuper) {
            trackInsuranceOptionSelected(insuranceEventProps);
          } else if (!willSuper && wasSuper) {
            trackInsuranceOptionDeselected(insuranceEventProps);
          }
        },
        onError: () => {
          alert(ALERT_MESSAGE_TYPE.ERROR_OCCURRENCE);
          setIsSwitchingTier(false);
        },
      },
    );
  };

  return (
    <LayoutWrapper isMobile={isMobile}>
      <ContentWrapper isMobile={isMobile}>
        <WhiteBgWrapper isMobile={isMobile}>
          {vehicleModelData && (
            <div>
              <VehicleDetailCard
                vehiclePrice={preReservationData?.listPrice}
                vehicleModel={vehicleModelData}
                isMobile={isMobile}
                pickUpAt={preReservationData?.pickUpAt}
                dropOffAt={preReservationData?.dropOffAt}
              />
            </div>
          )}
        </WhiteBgWrapper>
        <WhiteBgWrapper isMobile={isMobile}>
          <InsuranceBox isMobile={isMobile}>
            <InsuranceSectionTitle isMobile={isMobile}>
              자차보험 선택
            </InsuranceSectionTitle>
            <InsuranceTierSelector
              tiers={insuranceTiers}
              selected={selectedTier}
              onSelect={handleSelectTier}
              isMobile={isMobile}
              loading={isSwitchingTier}
            />
            <SuperNote>
              보험별 보상한도와 자기부담금 등 가입 조건을 확인해주세요.
            </SuperNote>
          </InsuranceBox>
        </WhiteBgWrapper>
        <WhiteBgWrapper isMobile={isMobile}>
          <CustomerForm {...customerFormProps} />
        </WhiteBgWrapper>
        {isWithPet && (
          <WhiteBgWrapper isMobile={isMobile}>
            <PetInfoForm
              value={petForm}
              onChange={setPetForm}
              isMobile={isMobile}
            />
          </WhiteBgWrapper>
        )}
        <WhiteBgWrapper isMobile={isMobile}>
          {!!user && (
            <MileageArea
              balance={mileageBalance}
              maxUsable={mileageCap}
              appliedMileage={appliedMileage}
              onApply={setAppliedMileage}
              onClear={() => setAppliedMileage(0)}
            />
          )}
          {/* [비활성화] 할인코드 DiscountArea — 주유권(InfluencerCodeArea)으로 대체
          <DiscountArea
            referralDiscountedPrice={discountCode?.discountAmount || 0}
            appliedReferralCode={appliedReferralCode}
            setAppliedReferralCode={setAppliedReferralCode}
            getPreReservation={getPreReservation}
          />
          */}
          {!!user && (
            <ReferralCodeArea
              discount={referralDiscount}
              onApplied={() => void refetchReferralDiscount()}
            />
          )}
          <InfluencerCodeArea onChange={setInfluencerCode} />
          <PaymentMethodArea
            paymentMethodList={PAYMENT_METHODS}
            selectedPaymentMethod={paymentMethod}
            setSelectedPaymentMethod={setPaymentMethod}
          />
        </WhiteBgWrapper>
        {isMobile && vehicleModelData && (
          <WhiteBgWrapper isMobile={isMobile}>
            <VehicleOptionSection vehicleModel={vehicleModelData} />
          </WhiteBgWrapper>
        )}
        {companyData?.rentalRules && (
          <WhiteBgWrapper isMobile={isMobile}>
            <RentPolicyBox isMobile={isMobile}>
              <RentPolicyTitle isMobile={isMobile}>대여규정</RentPolicyTitle>
              <RentPolicyContent
                ref={rentPolicyRef}
                collapsed={!isRentPolicyOpen && isRentPolicyOverflowing}
              >
                <Viewer
                  content={companyData.rentalRules}
                  style={{ lineHeight: '27px' }}
                />
              </RentPolicyContent>
              {/* DB 대여규정 본문에 없는 차급별 추가 대여 자격은 접힘 여부와 무관하게 항상 노출한다. */}
              <RentPolicyNotice>{SUPERCAR_RENTAL_AGE_NOTICE}</RentPolicyNotice>
              {isRentPolicyOverflowing && (
                <RentPolicyToggle
                  type='button'
                  onClick={() => setIsRentPolicyOpen((prev) => !prev)}
                >
                  {isRentPolicyOpen ? '접기' : '설명 더보기'}
                  <ToggleChevron open={isRentPolicyOpen} aria-hidden>
                    <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
                      <path
                        d='M4 6l4 4 4-4'
                        stroke='currentColor'
                        strokeWidth='1.6'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </ToggleChevron>
                </RentPolicyToggle>
              )}
            </RentPolicyBox>
          </WhiteBgWrapper>
        )}
      </ContentWrapper>

      <div
        ref={paymentCardRef}
        className={cn(
          'sticky top-36 h-fit w-96 rounded-m bg-grey-000 shadow-050',
          isMobile && 'w-full shadow-none',
        )}
      >
        <PaymentInfoCard
          isLogin={!!user}
          isMobile={isMobile}
          insurance={insuranceName}
          addInsurance={isFullCoverage && !isSuperInsurance}
          listPrice={preReservationData?.listPrice || 0}
          insurancePrice={
            preReservationData?.pricingVersion === 1
              ? preReservationData.insurancePrice
              : undefined
          }
          price={finalPrice}
          discountList={discountListWithMileage}
          fullCoverageInsurancePrice={
            preReservationData?.fullCoverageInsurancePrice || 0
          }
          onClick={handleClickPayButton}
          buttonLoading={isLoadingTossClient || isMutatingUpdatePreReservation}
          isCheckBoxChecked={checked}
          onCheckBoxClicked={handleCheckbox}
          memberDiscountRate={discountPolicyData?.discountValue}
          isMemberDiscountActive={
            !!discountPolicyData && discountPolicyData.isActive
          }
          excludeFullCoverageFromDiscount
          isPaymentPage
          platform='jejuro'
          animatePrice
          memberBenefitAmount={memberBenefitAmount}
          originalListPrice={
            rentalPromo.hasPromo ? rentalPromo.anchor : undefined
          }
          listPriceDiscountRate={
            rentalPromo.hasPromo ? rentalPromo.percentOff : undefined
          }
        />
      </div>

      {/* 모바일: 스크롤 위치와 무관하게 결제 금액을 항상 노출하는 하단 고정 바. */}
      {isMobile && (
        <MobilePayBar>
          <MobilePayAmount>
            <span>결제 금액</span>
            <b>
              {finalPrice.toLocaleString()}
              <em>원</em>
            </b>
          </MobilePayAmount>
          <MobilePayButton
            type='button'
            disabled={isLoadingTossClient || isMutatingUpdatePreReservation}
            onClick={() => {
              // 동의 전에 누른 것도 결제 시작으로 센다(눌렀는데 진행 못 한 경우를 가르기 위해).
              trackPaymentStarted();
              // 약관 미동의면 결제 카드(체크박스)로 이동, 동의 상태면 결제 진행.
              if (!checked) {
                paymentCardRef.current?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                });
                return;
              }
              handleClickPayButton();
            }}
          >
            {isLoadingTossClient || isMutatingUpdatePreReservation
              ? '처리 중…'
              : '결제하기'}
          </MobilePayButton>
        </MobilePayBar>
      )}
    </LayoutWrapper>
  );
};

export default PaymentLayout;

const LayoutWrapper = styled.div<{
  isMobile: boolean;
}>`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: ${({ isMobile }) => (isMobile ? '0' : '24px')};
  width: ${({ isMobile }) => (isMobile ? '100%' : '1200px')};
  /* 데스크탑 푸터가 absolute라 콘텐츠 하단이 푸터에 바짝 붙는다.
     사이드 결제정보 카드가 푸터와 겹쳐 보이지 않게 하단 여백을 확보한다. */
  padding-bottom: ${({ isMobile }) => (isMobile ? '0' : '80px')};
  /* 모바일 하단 고정 결제바에 콘텐츠가 가리지 않도록 하단 여백 확보. */
  ${({ isMobile }) =>
    isMobile && 'padding: 40px 0 calc(88px + env(safe-area-inset-bottom))'};
`;

const ContentWrapper = styled.div<{
  hasPadding?: boolean;
  isMobile?: boolean;
}>`
  width: ${({ isMobile }) => (isMobile ? '100%' : '792px')};
  scroll-margin: 150px;
`;

const MobilePayBar = styled.div`
  position: fixed;
  z-index: 40;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  background: ${({ theme }) => theme.COLOR.WHITE};
  border-top: 1px solid ${({ theme }) => theme.COLOR.GREY_05};
  box-shadow: 0 -6px 20px rgba(15, 27, 36, 0.1);
`;

const MobilePayAmount = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.15;

  span {
    ${({ theme }) => theme.FONT.B3};
    color: ${({ theme }) => theme.COLOR.GREY_02};
  }

  b {
    ${({ theme }) => theme.FONT.H3};
    font-weight: 800;
    color: ${({ theme }) => theme.COLOR.GREY_01};
    font-variant-numeric: tabular-nums;

    em {
      ${({ theme }) => theme.FONT.B2};
      font-style: normal;
      font-weight: 600;
      margin-left: 2px;
    }
  }
`;

const MobilePayButton = styled.button`
  margin-left: auto;
  flex: 0 0 auto;
  min-width: 140px;
  height: 50px;
  padding: 0 24px;
  border: none;
  border-radius: 12px;
  background: ${getPrimaryColor(JEJURO)};
  color: ${({ theme }) => theme.COLOR.WHITE};
  ${({ theme }) => theme.FONT.B1};
  font-weight: 800;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const RentPolicyBox = styled.div<{
  isMobile: boolean;
}>`
  width: 100%;
  padding: ${({ isMobile }) => (isMobile ? '20px 0' : '24px 0')};
`;

const InsuranceBox = styled.div<{ isMobile: boolean }>`
  width: 100%;
  padding: ${({ isMobile }) => (isMobile ? '20px 0' : '24px 0')};
`;

const InsuranceSectionTitle = styled.p<{ isMobile: boolean }>`
  margin-bottom: 16px;
  ${({ theme }) => theme.NEW_FONT.H4};
  font-weight: 700;
`;

const SuperNote = styled.p`
  margin: 12px 0 0;
  ${({ theme }) => theme.FONT.B3};
  color: ${({ theme }) => theme.COLOR.GREY_02};
  word-break: keep-all;
`;

const RentPolicyTitle = styled.p<{
  isMobile: boolean;
}>`
  margin-bottom: 16px;
  ${({ isMobile, theme }) =>
    isMobile ? theme.NEW_FONT.H4 : theme.NEW_FONT.H3};
`;

const RentPolicyContent = styled.div<{
  collapsed: boolean;
}>`
  position: relative;
  overflow: hidden;
  max-height: ${({ collapsed }) =>
    collapsed ? `${RENT_POLICY_PREVIEW_HEIGHT}px` : 'none'};

  ${({ collapsed, theme }) =>
    collapsed &&
    css`
      &::after {
        content: '';
        position: absolute;
        inset: auto 0 0 0;
        height: 72px;
        background: linear-gradient(
          to bottom,
          rgba(255, 255, 255, 0),
          ${theme.COLOR.WHITE}
        );
        pointer-events: none;
      }
    `}
`;

const RentPolicyNotice = styled.p`
  margin-top: 12px;
  ${({ theme }) => theme.FONT.B1};
  font-weight: 700;
  line-height: 27px;
  color: ${({ theme }) => theme.COLOR.RED};
  word-break: keep-all;
`;

const RentPolicyToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin-top: 16px;
  padding: 14px 0;
  border: 1px solid ${({ theme }) => theme.COLOR.GREY_05};
  border-radius: ${({ theme }) => theme.BORDER_RADIUS.LEVEL_01};
  background-color: ${({ theme }) => theme.COLOR.WHITE};
  color: ${({ theme }) => theme.COLOR.GREY_01};
  ${({ theme }) => theme.FONT.B1};
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.COLOR.GREY_BG};
  }
`;

const ToggleChevron = styled.span<{ open: boolean }>`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.COLOR.GREY_02};
  transition: transform 0.2s ease;
  transform: rotate(${({ open }) => (open ? '180deg' : '0deg')});
`;

const WhiteBgWrapper = styled.div<{
  isMobile: boolean;
}>`
  width: 100%;
  gap: 30px;
  padding: ${({ isMobile }) => (isMobile ? '0 20px' : '0 30px')};
  margin-bottom: ${({ isMobile }) => (isMobile ? '10px' : '24px')};
  background-color: ${({ theme }) => theme.COLOR.WHITE};
  background-color: white;
  border-radius: ${({ theme }) => theme.BORDER_RADIUS.LEVEL_02};

  & > *:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.COLOR.GREY_05};
  }

  & li {
    ${ListStyle}
  }
`;
